import { dateLib, moneyLib, tenantLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import { tenantType } from "@agentcall/core/type"
import { Edit2 } from "react-feather"
import { useNavigate, useParams } from "react-router-dom"
import { tenantApi } from "@/api/client/tenant"
import { EchoesEditGuard } from "@/components/echoes/EchoesEditGuard"
import { PropertyHeader } from "@/components/properties/PropertyHeader"
import { RoomHeader } from "@/components/properties/RoomHeader"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card/card"
import { Loading } from "@/components/ui/loading"

export function TenantDetailPage() {
  const navigate = useNavigate()
  const { propertyId: propertyIdParam, roomId: roomIdParam, tenantId: tenantIdParam } = useParams()
  const propertyId = propertyIdParam ? Number(propertyIdParam) : undefined
  const roomId = roomIdParam ? Number(roomIdParam) : undefined
  const tenantId = tenantIdParam ? Number(tenantIdParam) : undefined
  const hasValidIds = propertyId !== undefined && roomId !== undefined && tenantId !== undefined

  const {
    data: tenantResponse,
    isLoading,
    error,
  } = tenantApi.useTenantGet(
    tenantId,
    { withRoom: "true", withRoomProperty: "true" },
    {
      query: {
        enabled: hasValidIds,
      },
    }
  )

  const handleEdit = () => {
    if (propertyId !== undefined && roomId !== undefined && tenantId !== undefined) {
      navigate(`/properties/${propertyId}/rooms/${roomId}/tenants/${tenantId}/edit`)
    }
  }

  if (isLoading) {
    return <Loading className="mt-10" text="テナント情報を読み込んでいます..." />
  }

  if (!hasValidIds || error || !tenantResponse) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-slate-600">
          {hasValidIds
            ? "テナント情報の取得に失敗しました。"
            : "テナント情報が見つかりませんでした。"}
        </div>
        <Button onClick={() => navigate(-1)} variant="outline">
          前のページに戻る
        </Button>
      </div>
    )
  }

  const tenant = tenantResponse.tenant
  const isBusiness = tenant.purpose === tenantType.TENANT_PURPOSE_BUSINESS

  if (!propertyId || !roomId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">物件IDまたは部屋IDが不正です。</div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PropertyHeader propertyId={propertyId} />
      <RoomHeader roomId={roomId} />

      <div className="flex justify-end">
        <EchoesEditGuard>
          <Button onClick={handleEdit} variant="outline" size="sm" className="gap-2">
            <Edit2 className="h-4 w-4" />
            編集
          </Button>
        </EchoesEditGuard>
      </div>

      {/* 基本情報 */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-primary">基本情報</h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs font-semibold text-slate-500">氏名</span>
              <span className="text-sm font-semibold text-primary">{tenant.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs font-semibold text-slate-500">氏名（かな）</span>
              <span className="text-sm font-semibold text-primary">{tenant.nameKana}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs font-semibold text-slate-500">生年月日</span>
              <span className="text-sm font-semibold text-primary">
                {tenant.birthday ? dateLib.formatDate(tenant.birthday) : ""}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs font-semibold text-slate-500">用途</span>
              <span className="text-sm font-semibold text-primary">
                {tenantLib.getPurposeLabel(tenant.purpose)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs font-semibold text-slate-500">ステータス</span>
              <span className="text-sm font-semibold text-primary">
                {tenantLib.getStatusLabel(tenant)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 連絡先情報 */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-primary">連絡先情報</h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs font-semibold text-slate-500">電話番号</span>
              <span className="text-sm font-semibold text-primary">
                {formatPhone(tenant.phone1, tenant.phone2, tenant.phone3)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs font-semibold text-slate-500">メールアドレス</span>
              <span className="text-sm font-semibold text-primary">{tenant.email}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 契約情報 */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-primary">契約情報</h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs font-semibold text-slate-500">契約開始日</span>
              <span className="text-sm font-semibold text-primary">
                {dateLib.formatDate(tenant.startDate)}
              </span>
            </div>
            {tenant.endDate && (
              <div className="flex justify-between">
                <span className="text-xs font-semibold text-slate-500">契約終了日</span>
                <span className="text-sm font-semibold text-primary">
                  {dateLib.formatDate(tenant.endDate)}
                </span>
              </div>
            )}
            {tenant.endReason && (
              <div className="flex justify-between">
                <span className="text-xs font-semibold text-slate-500">終了理由</span>
                <span className="text-sm font-semibold text-primary">
                  {tenantType.TENANT_END_REASONS.find((reason) => reason.key === tenant.endReason)
                    ?.value ?? tenant.endReason}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 費用情報 */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-primary">費用情報</h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between items-start">
              <span className="text-xs font-semibold text-slate-500">賃料</span>
              <div className="flex flex-col items-end">
                <span className="text-sm font-semibold text-primary">
                  {isBusiness
                    ? moneyLib.calculateTaxIncluded(tenant.rent).toLocaleString()
                    : tenant.rent.toLocaleString()}
                  円
                </span>
                {isBusiness && (
                  <span className="text-xs text-slate-400">
                    ¥{tenant.rent.toLocaleString()} + 税¥
                    {moneyLib.calculateTax(tenant.rent).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs font-semibold text-slate-500">共益費</span>
              <div className="flex flex-col items-end">
                <span className="text-sm font-semibold text-primary">
                  {isBusiness
                    ? moneyLib.calculateTaxIncluded(tenant.commonAreaFee).toLocaleString()
                    : tenant.commonAreaFee.toLocaleString()}
                  円
                </span>
                {isBusiness && (
                  <span className="text-xs text-slate-400">
                    ¥{tenant.commonAreaFee.toLocaleString()} + 税¥
                    {moneyLib.calculateTax(tenant.commonAreaFee).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-xs font-semibold text-slate-500">駐車場利用</span>
              <span className="text-sm font-semibold text-primary">
                {tenant.isParking ? "あり" : "なし"}
              </span>
            </div>
            {tenant.isParking && (
              <>
                <div className="flex justify-between items-start">
                  <span className="text-xs font-semibold text-slate-500">駐車場料金</span>
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-semibold text-primary">
                      {moneyLib.calculateTaxIncluded(tenant.parkingFee).toLocaleString()}円
                    </span>
                    <span className="text-xs text-slate-400">
                      ¥{tenant.parkingFee.toLocaleString()} + 税¥
                      {moneyLib.calculateTax(tenant.parkingFee).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs font-semibold text-slate-500">駐車場番号</span>
                  <span className="text-sm font-semibold text-primary">{tenant.parkingNumber}</span>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* その他の費用 */}
      {tenant.otherFees && tenant.otherFees.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-primary">その他の費用</h2>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {tenant.otherFees.map((fee) => (
                <div key={fee.label} className="flex justify-between">
                  <span className="text-xs font-semibold text-slate-500">{fee.label}</span>
                  <span className="text-sm font-semibold text-primary">
                    {(fee.price + fee.tax).toLocaleString()}円
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 管理メモ */}
      {tenant.adminMemo && (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-primary">管理メモ</h2>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{tenant.adminMemo}</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
